import {
  alpha,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Fade,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  ArrowBack as BackIcon,
  Business as BusinessIcon,
  ChevronRight as ChevronRightIcon,
  ConfirmationNumberOutlined as CaseIcon,
  DescriptionOutlined as ContractIcon,
  Handyman as ServiceIcon,
  PersonOutline as CustomerIcon,
  ReceiptLongOutlined as InvoiceIcon,
  SolarPower as SolarIcon,
  SupportAgent as SupportIcon,
  TrendingUp as SalesIcon,
} from "@mui/icons-material";
import React, { useState } from "react";
import {
  useCreateWorkMutation,
  useGetCrmCategoriesQuery,
  useGetCrmTeamsQuery,
} from "../../store/services/crmApi";
import { useGetCurrentUserQuery } from "../../store/services/userService";
import { useGetCustomerByIdQuery } from "../../store/services/customerService";
import { CrmCategory } from "../../store/types/crm.types";
import { Work, WorkStateEnum, WorkTypeEnum } from "../../store/types/work.types";
import { useWorkPanel } from "../../hooks/useWorkPanel";

interface CreateCaseDialogProps {
  open: boolean;
  customerNo: number;
  onClose: () => void;
  customerName?: string;
}

/** Ikon + färg per toppkategori (faller tillbaka på neutral default). */
const CASE_META: Record<number, { icon: React.ReactNode; color: string }> = {
  3000: { icon: <ContractIcon />, color: "#1976d2" }, // Avtal
  3100: { icon: <InvoiceIcon />, color: "#c62828" }, // Faktura
  3200: { icon: <CustomerIcon />, color: "#6a1b9a" }, // Kunduppgifter
  3300: { icon: <SupportIcon />, color: "#00838f" }, // Support
  3500: { icon: <SolarIcon />, color: "#f9a825" }, // Solceller
  3600: { icon: <ServiceIcon />, color: "#5d4037" }, // Tjänster
  3800: { icon: <SalesIcon />, color: "#2e7d32" }, // Säljinsats
  3900: { icon: <BusinessIcon />, color: "#283593" }, // Företagsförsäljning
};

const metaFor = (id: number) =>
  CASE_META[id] ?? { icon: <CaseIcon />, color: "#546e7a" };

/** En toppkategori går att välja direkt om den har egna förval. */
const isSelectableParent = (cat: CrmCategory) =>
  cat.DefaultTeamId != null ||
  (cat.DefaultTitle != null && cat.DefaultTitle.length > 0);

const CreateCaseDialog: React.FC<CreateCaseDialogProps> = ({
  open,
  customerNo,
  onClose,
  customerName,
}) => {
  const { data: categories = [], isLoading: catsLoading } =
    useGetCrmCategoriesQuery(undefined, { skip: !open });
  const { data: crmTeams = [] } = useGetCrmTeamsQuery(undefined, {
    skip: !open,
  });
  const { data: currentUser } = useGetCurrentUserQuery(undefined, {
    skip: !open,
  });
  const { data: customer } = useGetCustomerByIdQuery(customerNo, {
    skip: !open,
  });

  const teams = crmTeams.filter((t) => t.TeamSelectable);

  const [createWork, { isLoading: isCreating }] = useCreateWorkMutation();
  const { openWork } = useWorkPanel();

  const [topId, setTopId] = useState<number | null>(null);
  const [leafId, setLeafId] = useState<number | null>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [teamId, setTeamId] = useState<number | "">("");
  const [assignSelf, setAssignSelf] = useState(false);

  const top = categories.find((c) => c.Id === topId) ?? null;
  const leaf: CrmCategory | null =
    leafId == null
      ? null
      : leafId === topId
        ? top
        : (top?.Categories.find((c) => c.Id === leafId) ?? null);

  /** Initiera formuläret från vald lövkategori med fallback på toppkategorin. */
  const enterForm = (chosen: CrmCategory, parent: CrmCategory) => {
    setLeafId(chosen.Id);
    setTitle(chosen.DefaultTitle || "");
    setTeamId(chosen.DefaultTeamId ?? parent.DefaultTeamId ?? "");
    setAssignSelf(chosen.DefaultAssignSelf ?? parent.DefaultAssignSelf ?? false);
  };

  const resetSelections = () => {
    setTopId(null);
    setLeafId(null);
    setTitle("");
    setDescription("");
    setTeamId("");
    setAssignSelf(false);
  };

  const handleClose = () => {
    resetSelections();
    onClose();
  };

  const handleSelectTop = (cat: CrmCategory) => {
    setTopId(cat.Id);
    setLeafId(null);
    // Lövnod utan barn → hoppa direkt till formuläret.
    if (cat.Categories.length === 0) enterForm(cat, cat);
  };

  const handleBack = () => {
    if (leafId != null && top && top.Categories.length > 0) {
      // Tillbaka från formulär till underkategori-listan.
      setLeafId(null);
      setTitle("");
      setDescription("");
      setTeamId("");
      setAssignSelf(false);
    } else {
      // Tillbaka till toppkategori-listan.
      resetSelections();
    }
  };

  const nameTrimmed = title.trim();
  const canSubmit = !!leaf && nameTrimmed.length >= 2 && teamId !== "";

  const handleSubmit = async () => {
    if (!leaf || teamId === "") return;
    try {
      const created = await createWork({
        Name: nameTrimmed,
        TeamId: teamId,
        CategoryId: leaf.Id,
        Type: WorkTypeEnum.Other,
        WorkState: WorkStateEnum.Open,
        Description: description.trim() || undefined,
        CustomerId: customerNo,
        // -1 = ej tilldelad (backend sparar som null).
        AssignedToId: assignSelf ? (currentUser?.ExternalUserId ?? -1) : -1,
      }).unwrap();

      // Öppna det nyskapade ärendet direkt. Svaret är antingen hela Work-objektet
      // eller bara dess id, så vi hanterar båda.
      const result = created as Work | number;
      const newWorkId = typeof result === "number" ? result : result?.Id;
      if (newWorkId) openWork(newWorkId);

      handleClose();
    } catch (err) {
      // Toast hanteras i mutationen; logga för felsökning.
      console.error("Kunde inte skapa ärende:", err);
    }
  };

  const showForm = leaf != null;
  const showSubList = top != null && !showForm && top.Categories.length > 0;
  const showTopList = top == null;

  const suggestions = leaf?.NameSuggestions ?? [];

  // Underkategori-lista: toppkategorin själv först (om den är valbar), sedan barn.
  const subItems: CrmCategory[] = top
    ? [...(isSelectableParent(top) ? [top] : []), ...top.Categories]
    : [];

  const headerColor = top ? metaFor(top.Id).color : "#546e7a";

  const headerTitle = showForm
    ? "Nytt ärende"
    : showSubList
      ? top!.Name
      : "Skapa ärende";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3,
          py: 2,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          bgcolor: top ? alpha(headerColor, 0.06) : "transparent",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        {top != null && (
          <IconButton size="small" onClick={handleBack} sx={{ mr: 0.5 }}>
            <BackIcon fontSize="small" />
          </IconButton>
        )}
        <Avatar
          sx={{
            width: 36,
            height: 36,
            bgcolor: alpha(headerColor, 0.12),
            color: headerColor,
          }}
        >
          {top ? metaFor(top.Id).icon : <CaseIcon />}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
            {headerTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {showForm
              ? leaf!.Name
              : customer?.Name || customerName || "Välj kategori"}
          </Typography>
        </Box>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        {catsLoading ? (
          <Box display="flex" justifyContent="center" py={5}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            {/* Steg 1: toppkategori */}
            {showTopList && (
              <Fade in>
                <List disablePadding>
                  {categories.map((cat, index) => {
                    const m = metaFor(cat.Id);
                    const hasChildren = cat.Categories.length > 0;
                    return (
                      <React.Fragment key={cat.Id}>
                        {index > 0 && <Divider />}
                        <ListItemButton
                          onClick={() => handleSelectTop(cat)}
                          sx={{
                            px: 3,
                            py: 1.75,
                            "&:hover": { bgcolor: alpha(m.color, 0.05) },
                          }}
                        >
                          <ListItemAvatar>
                            <Avatar
                              sx={{
                                bgcolor: alpha(m.color, 0.1),
                                color: m.color,
                                width: 44,
                                height: 44,
                              }}
                            >
                              {m.icon}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography fontWeight={600}>
                                {cat.Name}
                              </Typography>
                            }
                            secondary={
                              hasChildren
                                ? `${cat.Categories.length} underkategorier`
                                : "Skapa direkt"
                            }
                            sx={{ ml: 1 }}
                          />
                          <ChevronRightIcon sx={{ color: "text.disabled" }} />
                        </ListItemButton>
                      </React.Fragment>
                    );
                  })}
                </List>
              </Fade>
            )}

            {/* Steg 2: underkategori */}
            {showSubList && (
              <Fade in>
                <List disablePadding>
                  {subItems.map((sub, index) => {
                    const isParent = sub.Id === top!.Id;
                    return (
                      <React.Fragment key={sub.Id}>
                        {index > 0 && <Divider variant="inset" />}
                        <ListItemButton
                          onClick={() => enterForm(sub, top!)}
                          sx={{
                            px: 3,
                            py: 1.5,
                            "&:hover": {
                              bgcolor: alpha(headerColor, 0.05),
                            },
                          }}
                        >
                          <ListItemAvatar>
                            <Avatar
                              sx={{
                                bgcolor: alpha(headerColor, 0.1),
                                color: headerColor,
                                width: 38,
                                height: 38,
                                fontSize: 15,
                                fontWeight: 600,
                              }}
                            >
                              {isParent ? metaFor(top!.Id).icon : sub.Name[0]}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={isParent ? `Allmänt – ${sub.Name}` : sub.Name}
                            sx={{ ml: 0.5 }}
                          />
                          <ChevronRightIcon
                            sx={{ color: "text.disabled", fontSize: 20 }}
                          />
                        </ListItemButton>
                      </React.Fragment>
                    );
                  })}
                </List>
              </Fade>
            )}

            {/* Steg 3: formulär */}
            {showForm && (
              <Fade in>
                <Stack spacing={2.5} sx={{ p: 3 }}>
                  <TextField
                    label="Titel"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    fullWidth
                    size="small"
                    placeholder={leaf!.Name}
                  />
                  {suggestions.length > 0 && (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                      {suggestions.map((s) => (
                        <Chip
                          key={s}
                          label={s}
                          clickable
                          size="small"
                          variant={title === s ? "filled" : "outlined"}
                          color={title === s ? "primary" : "default"}
                          onClick={() => setTitle(s)}
                        />
                      ))}
                    </Box>
                  )}

                  <TextField
                    label="Beskrivning"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    multiline
                    rows={3}
                    fullWidth
                    placeholder="Beskriv ärendet..."
                  />

                  <FormControl size="small" fullWidth>
                    <InputLabel id="case-team-label">Team</InputLabel>
                    <Select
                      labelId="case-team-label"
                      label="Team"
                      value={teamId}
                      onChange={(e) =>
                        setTeamId(
                          e.target.value === "" ? "" : Number(e.target.value)
                        )
                      }
                    >
                      <MenuItem value="">
                        <em>Inget team</em>
                      </MenuItem>
                      {/* Förvalt team kan saknas i listan – visa det ändå. */}
                      {teamId !== "" &&
                        !teams.some((t) => t.Id === teamId) && (
                          <MenuItem value={teamId}>
                            {crmTeams.find((t) => t.Id === teamId)?.Name ??
                              `Team ${teamId}`}{" "}
                            (förvalt)
                          </MenuItem>
                        )}
                      {teams.map((t) => (
                        <MenuItem key={t.Id} value={t.Id}>
                          {t.Name}
                        </MenuItem>
                      ))}
                    </Select>
                    {teamId === "" && (
                      <FormHelperText>
                        Välj ett team för att skapa ärendet
                      </FormHelperText>
                    )}
                  </FormControl>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={assignSelf}
                        onChange={(e) => setAssignSelf(e.target.checked)}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        Tilldela mig
                        {currentUser?.Name ? ` (${currentUser.Name})` : ""}
                      </Typography>
                    }
                  />
                </Stack>
              </Fade>
            )}
          </>
        )}
      </DialogContent>

      {showForm && (
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Button onClick={handleClose} disabled={isCreating}>
            Avbryt
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!canSubmit || isCreating}
            sx={{ borderRadius: 2, px: 3 }}
          >
            {isCreating ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Skapa ärende"
            )}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default CreateCaseDialog;
